import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import { Dialog } from '@capacitor/dialog';
import { Preferences } from '@capacitor/preferences';
import { ActionPerformed, PushNotificationSchema, PushNotifications, Token } from '@capacitor/push-notifications';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IlopNotification } from '../models/ilop-notification.model';

const NOTIFICATION_KEY = 'notification-token';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private httpClient = inject(HttpClient);
  private router = inject(Router);
  private translateService = inject(TranslateService);

  refreshHasNotifications$ = new Subject<void>();

  constructor() {
    this.registerListeners();
  }
  async initialize() {
    if ((await Preferences.get({ key: NOTIFICATION_KEY })).value) {
      return;
    }

    if (Capacitor.getPlatform() === 'web') return;

    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        PushNotifications.register();
      }
    });

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', async (token: Token) => {
      await Preferences.set({ key: NOTIFICATION_KEY, value: token.value });
      this.savePersonNotificationToken(token.value).subscribe();
    });
  }

  getAllNotifications(): Observable<IlopNotification[]> {
    return this.httpClient.get<IlopNotification[]>(`${environment.backendUrl}v1/MyNotifications`);
  }

  patchMarkNotificationAsRead(notificationId: string): Observable<void> {
    return this.httpClient.patch<void>(`${environment.backendUrl}v1/MyNotifications/${notificationId}/read`, null);
  }

  patchMarkAllNotificationsAsRead(): Observable<void> {
    return this.httpClient.patch<void>(`${environment.backendUrl}v1/MyNotifications/read`, null);
  }

  getAllNotificationsCount(): Observable<number> {
    return this.httpClient.get<number>(`${environment.backendUrl}v1/MyNotifications/Count`);
  }

  deleteNotification(notificationId: string): Observable<void> {
    return this.httpClient.delete<void>(`${environment.backendUrl}v1/MyNotifications/${notificationId}`);
  }

  private registerListeners() {
    if (Capacitor.getPlatform() === 'web') return;

    PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      this.refreshHasNotifications$.next();
      if (notification.body) {
        if (notification.data.destination) {
          const { value: confirmed } = await Dialog.confirm({
            title: notification.title,
            message: notification.body,
            cancelButtonTitle: this.translateService.instant('PUSH_NOTIFICATIONS.CLOSE'),
            okButtonTitle: this.translateService.instant('PUSH_NOTIFICATIONS.OPEN'),
          });

          if (confirmed) {
            this.router.navigateByUrl(notification.data.destination);
          }
        } else {
          await Dialog.alert({
            title: notification.title,
            message: notification.body,
            buttonTitle: this.translateService.instant('PUSH_NOTIFICATIONS.CLOSE'),
          });
        }
      }
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      if (notification.notification.data?.destination) {
        this.router.navigateByUrl(notification.notification.data.destination);
      }
    });
  }

  unregister() {
    Preferences.remove({ key: NOTIFICATION_KEY });
  }

  async getNotificationToken() {
    return await Preferences.get({ key: NOTIFICATION_KEY });
  }

  savePersonNotificationToken(token: string) {
    return this.httpClient.post(`${environment.backendUrl}v1/currentPerson/notificationToken`, { value: token });
  }
}
